import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Typography } from '@mui/material';
import { IotTourPopupProps } from './types';
import { ContentSection } from './styles';
import { TourPopup } from 'src/components/popup/TourPopup';
import truncate from 'src/components/tag-popup/utils';
import { TourActionType } from 'src/sdk-components/VSObject';
import { convertMDLink } from 'src/utils/convertMDLink';
import { renderMediaContent } from 'src/components/tag-popup/config';
import { DeviceManager } from 'src/utils';
import { GalleryButton } from 'src/components/gallery/styles';
import Icon from 'src/units/Icon';

export const TagPopup: FC<IotTourPopupProps & { children?: any }> = ({
  open,
  setOpen,
  showDescription,
  tag,
  pauseTourMusic,
  playTourMusic,
  translation = {},
}) => {
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const containerRef = useRef<any>();
  const [inFullScreen, setInFullScreen] = useState(false);
  const hasVideoUrl =
    (tag?.showVideoUrl && tag?.videoUrl && tag?.videoUrl !== 'null') ||
    (Array.isArray(tag?.tagGalleries) &&
      tag.tagGalleries.some(
        (item) => typeof item?.url === 'string' && item.url.match(/\.(mp4|mov|avi|webm|mkv)$/i),
      ));

  const handleReadMoreClick = () => {
    setDescriptionExpanded(true);
  };

  const handleReadLessClick = () => {
    setDescriptionExpanded(false);
  };

  const trimmedDescription = useMemo(() => {
    if (!tag?.description) return '';
    return truncate(tag.description, 650);
  }, [tag]);

  const handleSetOpen = (value: boolean) => {
    if (!value) setDescriptionExpanded(false);
    setOpen(value, false);
  };

  const toggleFullScreen = (ref) => {
    if (!document.fullscreenElement) {
      if (ref.current.requestFullscreen) {
        ref.current
          .requestFullscreen()
          .then(() => {
            setInFullScreen(true);
          })
          .catch((err) => {
            setInFullScreen(false);
            alert(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
          });
      }
    } else {
      if (document.exitFullscreen) {
        setInFullScreen(false);
        document.exitFullscreen();
      }
    }
  };
  const handleFullScreenExit = () => {
    if (!document.fullscreenElement) {
      setInFullScreen(false);
    }
  };

  useEffect(() => {
    if (hasVideoUrl && pauseTourMusic) {
      pauseTourMusic();
    }
    document.addEventListener('fullscreenchange', handleFullScreenExit);
    return () => {
      if (hasVideoUrl && playTourMusic) {
        playTourMusic();
      }
      document.removeEventListener('fullscreenchange', handleFullScreenExit);
    };
  }, []);

  useEffect(() => {
    setDescriptionExpanded(showDescription);
  }, [showDescription]);

  // Show just the Iframe in the popup if it's together with an just an image
  const hideImageInPopup =
    tag?.type === TourActionType.Iframe &&
    tag?.tagGalleries?.length === 1 &&
    tag?.tagGalleries[0]?.isImage;

  return (
    <TourPopup
      open={open}
      setOpen={handleSetOpen}
      title={tag?.mpTitle && tag?.title}
      type={tag?.type}
    >
      {tag?.description ? (
        <ContentSection>
          <Typography
            dangerouslySetInnerHTML={{
              __html: descriptionExpanded
                ? convertMDLink(tag.description)
                : convertMDLink(trimmedDescription),
            }}
            sx={{
              fontSize: '14px',
              whiteSpace: 'pre-wrap',
              '& em': {
                fontStyle: 'italic',
              },
              '& s': {
                textDecoration: 'line-through',
              },
            }}
          ></Typography>
          {!descriptionExpanded && trimmedDescription !== tag.description ? (
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 'bold',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
              onClick={handleReadMoreClick}
            >{` ${translation?.readMore || 'Read more'}`}</Typography>
          ) : null}
          {descriptionExpanded && trimmedDescription !== tag?.description ? (
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 'bold',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
              onClick={handleReadLessClick}
            >{` ${translation?.readLess || 'Read less'}`}</Typography>
          ) : null}
        </ContentSection>
      ) : null}
      {tag?.iotDevices?.length ? (
        <ContentSection>
          <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>IoT</Typography>
        </ContentSection>
      ) : null}
      {!hideImageInPopup &&
      tag?.type !== TourActionType.Iframe &&
      (tag?.tagGalleries?.length || (tag?.videoUrl && tag.videoUrl !== 'null')) ? (
        <ContentSection>
          {renderMediaContent(tag, inFullScreen, toggleFullScreen, pauseTourMusic)}
        </ContentSection>
      ) : null}
      {tag?.type === TourActionType.Iframe && (
        <ContentSection>
          <div
            ref={containerRef}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {inFullScreen ? (
              <GalleryButton
                style={{
                  position: 'fixed',
                  top: DeviceManager.isMobile() ? '14px' : `30px`,
                  right: DeviceManager.isMobile() ? '14px' : '30px',
                  background: 'rgba(0, 0, 0, 0.1)',
                  // @ts-ignore
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  },
                  // @ts-ignore
                  ' &:focus': {
                    backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  },
                }}
                onClick={() => toggleFullScreen(containerRef)}
              >
                <Icon.Close
                  style={{ color: 'white', width: '100%', height: '100%' }}
                  fontSize="large"
                />
              </GalleryButton>
            ) : (
              <GalleryButton
                style={{
                  position: 'absolute',
                  bottom: `${20}px`,
                  right: '30px',
                  background: 'rgba(0, 0, 0, 0.1)',
                  // @ts-ignore
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  },
                  // @ts-ignore
                  ' &:focus': {
                    backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  },
                }}
                onClick={() => toggleFullScreen(containerRef)}
              >
                <Icon.FullScreen />
              </GalleryButton>
            )}
            <iframe
              style={{
                width: inFullScreen
                  ? '100%'
                  : !DeviceManager.isMobile() || DeviceManager.isHorizontalMobileView()
                    ? '60vw'
                    : 'auto',
                height: inFullScreen ? '100%' : '70dvh',
              }}
              allow="fullscreen; camera; microphone; clipboard-read; clipboard-write; display-capture; geolocation; xr-spatial-tracking"
              src={
                DeviceManager.isMobile() && tag.url?.endsWith('.pdf')
                  ? `https://docs.google.com/viewerng/viewer?url=${tag.url}&embedded=true`
                  : tag.url
              }
            ></iframe>
          </div>
        </ContentSection>
      )}
    </TourPopup>
  );
};
